import React, { useState } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Tooltip,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Save, Edit3, Plus, Settings, X, Check, Trash2 } from 'lucide-react';

import SafeChart from './common/SafeChart';
import EditableInput from './common/EditableInput';
import { CHART_COLORS as COLORS } from './common/Constants';
import AffiliateCard from './common/AffiliateCard';
import { AFFILIATE_LINKS } from '../config/affiliateLinks';

/**
 * InvestmentsTab – enhanced version with categories, monthly tracking, and growth charts.
 *
 * Props
 *  ─ investments:          Investment[]
 *  ─ totalInvestments:     number (pre-calculated for speed)
 *  ─ editMode:             boolean
 *  ─ onToggleEdit():       handler
 *  ─ updateInvestment(i,k,val)
 *  ─ addInvestment():      append a blank investment row
 *  ─ chartsReady:          boolean
 *  ─ investmentCategories: string[] - available categories
 *  ─ onUpdateCategories:   handler to update categories
 *  ─ investmentHistory:    object with monthly data by year
 *  ─ updateInvestmentHistory: handler to update monthly values
 */
const InvestmentsTab = ({
  investments,
  totalInvestments,
  editMode,
  onToggleEdit,
  updateInvestment,
  addInvestment,
  chartsReady,
  investmentCategories = ['Emergency Fund', 'Retirement', 'Growth', 'Income', 'Speculative', 'Real Estate', 'Crypto'],
  onUpdateCategories,
  investmentHistory = {},
  updateInvestmentHistory,
  removeInvestment, // Add this prop
}) => {
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [tempCategories, setTempCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showGrowthChart, setShowGrowthChart] = useState(true);

  // Cache the filtered investments with their original indices
  const filteredInvestmentsWithIndices = React.useMemo(() => {
    return investments
      .map((inv, originalIndex) => ({ inv, originalIndex }))
      .filter(({ inv }) => !inv.name.includes('[DELETED]') && inv.amount >= 0);
  }, [investments]);

  // Get available years from investment history
  const availableYears = Object.keys(investmentHistory).map(year => parseInt(year)).sort((a, b) => b - a);
  if (availableYears.length === 0) {
    availableYears.push(new Date().getFullYear());
  }

  // Prepare growth chart data for selected year
  const getGrowthChartData = () => {
    const yearData = investmentHistory[selectedYear] || {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return months.map(month => {
      const isCurrentMonth = month === new Date().toLocaleDateString('en-US', { month: 'short' }) && selectedYear === new Date().getFullYear();
      return {
        month,
        total: isCurrentMonth ? totalInvestments : (yearData[month] || 0),
      };
    });
  };

  // Calculate category totals for summary
  const getCategorySummary = () => {
    const categoryTotals = {};
    investments
      .filter(inv => !inv.name.includes('[DELETED]') && inv.amount > 0) // Filter out deleted investments
      .forEach(inv => {
        const category = inv.type || 'Uncategorized';
        categoryTotals[category] = (categoryTotals[category] || 0) + (inv.amount || 0);
      });
    
    return Object.entries(categoryTotals)
      .map(([category, total]) => ({ category, total }))
      .sort((a, b) => b.total - a.total);
  };

  // Prepare pie chart data grouped by category
  const getPieChartData = () => {
    const categoryTotals = {};
    investments
      .filter(inv => !inv.name.includes('[DELETED]') && inv.amount > 0)
      .forEach(inv => {
        const category = inv.type || 'Uncategorized';
        categoryTotals[category] = (categoryTotals[category] || 0) + (inv.amount || 0);
      });
    
    return Object.entries(categoryTotals)
      .map(([category, amount]) => ({ name: category, amount }))
      .sort((a, b) => b.amount - a.amount);
  };

  // Category editor functions
  const openCategoryEditor = () => {
    setTempCategories([...investmentCategories]);
    setShowCategoryEditor(true);
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    
    const categoryName = newCategoryName.trim();
    if (tempCategories.includes(categoryName)) {
      alert('Category already exists!');
      return;
    }
    
    setTempCategories([...tempCategories, categoryName]);
    setNewCategoryName('');
  };

  const handleRemoveCategory = (categoryToRemove) => {
    setTempCategories(tempCategories.filter(cat => cat !== categoryToRemove));
  };

  const handleSaveCategories = () => {
    if (onUpdateCategories) {
      onUpdateCategories(tempCategories);
    }
    setShowCategoryEditor(false);
  };

  const handleCancelCategories = () => {
    setTempCategories([]);
    setNewCategoryName('');
    setShowCategoryEditor(false);
  };

  const moveCategory = (index, direction) => {
    const newCategories = [...tempCategories];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newCategories.length) {
      [newCategories[index], newCategories[newIndex]] = [newCategories[newIndex], newCategories[index]];
      setTempCategories(newCategories);
    }
  };

  // Add investment with default Emergency Fund category
  const handleAddInvestment = () => {
    const newInvestment = {
      name: 'New Investment',
      type: 'Emergency Fund', // Default to Emergency Fund
      amount: 0
    };
    addInvestment(newInvestment);
  };

  // Remove investment — uses parent's hard-delete handler (filters from array entirely)
  const handleRemoveInvestment = (index) => {
    if (window.confirm('Are you sure you want to remove this investment?')) {
      if (removeInvestment) {
        removeInvestment(index);
      } else {
        // Fallback: legacy soft-delete if parent didn't pass remove handler
        updateInvestment(index, 'amount', 0);
        updateInvestment(index, 'name', '[DELETED] ' + investments[index].name);
      }
    }
  };

  // Auto-update investment history based on current total
  const syncCurrentTotal = () => {
    const currentMonth = new Date().toLocaleDateString('en-US', { month: 'short' });
    const currentYear = new Date().getFullYear();
    
    if (updateInvestmentHistory) {
      updateInvestmentHistory(currentYear, currentMonth, totalInvestments);
    }
  };

  // Sync current investments total to growth chart
  React.useEffect(() => {
    if (totalInvestments > 0) {
      syncCurrentTotal();
    }
  }, [totalInvestments]);

  // Update monthly investment value
  const updateMonthlyValue = (month, value) => {
    if (updateInvestmentHistory) {
      updateInvestmentHistory(selectedYear, month, parseFloat(value) || 0);
    }
  };

  const growthChartData = getGrowthChartData();
  const categorySummary = getCategorySummary();
  const pieChartData = getPieChartData();

  return (
    <div className="space-y-6">
      <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
          <h3 className="text-lg font-semibold">Investment Portfolio</h3>

          <div className="flex gap-2">
            <button
              onClick={openCategoryEditor}
              className="flex items-center gap-2 px-3 py-2 rounded-lg btn-outline border-arc-line"
            >
              <Settings size={16} /> Categories
            </button>

            <button
              onClick={handleAddInvestment}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 text-white hover:opacity-90"
            >
              <Plus size={16} /> Add&nbsp;Investment
            </button>

            <button
              onClick={() => setShowGrowthChart(!showGrowthChart)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:opacity-90"
            >
              {showGrowthChart ? 'Hide' : 'Show'} Growth Chart
            </button>

            <button
              onClick={onToggleEdit}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                editMode ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
              } hover:opacity-90`}
            >
              {editMode ? <Save size={16} /> : <Edit3 size={16} />}{' '}
              {editMode ? 'Save' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Category Summary */}
        <div className="mb-6 p-4 bg-white/[0.03] rounded-lg">
          <h4 className="text-lg font-semibold mb-3">Portfolio by Category</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {categorySummary.map(({ category, total }, index) => (
              <div key={category} className="bg-arc-elevated p-3 rounded border">
                <div className="flex items-center gap-2 mb-1">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm font-medium">{category}</span>
                </div>
                <p className="text-lg font-bold text-blue-600">
                  ${total.toLocaleString()}
                </p>
                <p className="text-xs text-arc-dim">
                  {((total / totalInvestments) * 100).toFixed(1)}% of portfolio
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Category Editor Modal */}
        {showCategoryEditor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-arc-elevated rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold">Customize Investment Categories</h4>
                <button
                  onClick={handleCancelCategories}
                  className="text-arc-dim hover:text-arc-muted"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Add New Category */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Add New Category</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Category name"
                    className="flex-1 px-3 py-2 border border-arc-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  />
                  <button
                    onClick={handleAddCategory}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Current Categories */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Current Categories</label>
                <div className="space-y-2">
                  {tempCategories.map((category, index) => (
                    <div key={category} className="flex items-center gap-2 p-2 bg-white/[0.03] rounded">
                      <span className="flex-1">{category}</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveCategory(index, 'up')}
                          disabled={index === 0}
                          className="px-2 py-1 text-sm bg-white/[0.08] rounded hover:bg-white/10 disabled:opacity-50"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveCategory(index, 'down')}
                          disabled={index === tempCategories.length - 1}
                          className="px-2 py-1 text-sm bg-white/[0.08] rounded hover:bg-white/10 disabled:opacity-50"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => handleRemoveCategory(category)}
                          className="px-2 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleCancelCategories}
                  className="px-4 py-2 btn-outline border-arc-line rounded-lg hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategories}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Check size={16} /> Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Growth Chart Section */}
        {showGrowthChart && (
          <div className="mb-6 p-4 bg-white/[0.03] rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">Investment Growth</h4>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-1 border border-arc-line rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Monthly Values Editor (only in edit mode) */}
            {editMode && (
              <div className="mb-4">
                <h5 className="text-sm font-medium mb-2">Monthly Values for {selectedYear}</h5>
                <div className="mb-2 p-2 bg-blue-50 rounded text-sm">
                  <p><strong>💡 Tip:</strong> Your current portfolio total (${totalInvestments.toLocaleString()}) is automatically synced to the current month. Edit other months to track historical growth.</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => {
                    const isCurrentMonth = month === new Date().toLocaleDateString('en-US', { month: 'short' }) && selectedYear === new Date().getFullYear();
                    return (
                      <div key={month} className="flex flex-col">
                        <label className={`text-xs mb-1 ${isCurrentMonth ? 'text-blue-600 font-semibold' : 'text-arc-muted'}`}>
                          {month} {isCurrentMonth && '(Current)'}
                        </label>
                        <EditableInput
                          value={isCurrentMonth ? totalInvestments : (investmentHistory[selectedYear]?.[month] || 0)}
                          onChange={(val) => updateMonthlyValue(month, val)}
                          className={`w-full text-right text-sm ${isCurrentMonth ? 'bg-blue-50 border-blue-300' : ''}`}
                          disabled={isCurrentMonth}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Line Chart */}
            <SafeChart chartsReady={chartsReady}>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={growthChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Total Value']} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    name="Portfolio Value"
                  />
                </LineChart>
              </ResponsiveContainer>
            </SafeChart>
          </div>
        )}

        {/* Layout grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie chart - Now shows categories instead of individual investments */}
          <SafeChart chartsReady={chartsReady}>
            <ResponsiveContainer width="100%" height={450}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={130}
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  dataKey="amount"
                  nameKey="name"
                  fill="#8884d8"
                >
                  {pieChartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </SafeChart>

          {/* Editable list */}
          <div className="space-y-2">
            {filteredInvestmentsWithIndices.map(({ inv, originalIndex }) => (
              <div
                key={originalIndex}
                className="flex justify-between items-center p-3 border rounded"
              >
                <div className="flex-1">
                  <p className="font-medium">
                    {editMode ? (
                      <EditableInput
                        type="text"
                        value={inv.name}
                        onChange={(v) => updateInvestment(originalIndex, 'name', v)}
                        className="font-medium w-full"
                      />
                    ) : (
                      inv.name
                    )}
                  </p>
                  <div className="text-sm text-arc-muted flex items-center gap-2">
                    {editMode ? (
                      <select
                        value={inv.type}
                        onChange={(e) => updateInvestment(originalIndex, 'type', e.target.value)}
                        className="text-sm border border-arc-line rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {investmentCategories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {inv.type}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-blue-600">
                    {editMode ? (
                      <EditableInput
                        value={inv.amount}
                        onChange={(v) => updateInvestment(originalIndex, 'amount', v)}
                        className="w-24 text-right"
                      />
                    ) : (
                      `$${inv.amount.toLocaleString()}`
                    )}
                  </p>
                  {editMode && (
                    <button
                      onClick={() => handleRemoveInvestment(originalIndex)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove investment"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total */}
        <div className="mt-4 text-center p-4 bg-blue-50 rounded-lg">
          <p className="text-arc-muted text-sm">Total Portfolio Value</p>
          <p className="text-3xl font-bold text-blue-600">
            ${totalInvestments.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Contextual recommendations: where to actually buy crypto.
          Investments tab is high-intent — users here are looking at where
          their money sits and where to put more. */}
      {(() => {
        const exchanges = AFFILIATE_LINKS.filter(
          (l) => !l.skip && l.category === "exchanges"
        );
        if (exchanges.length === 0) return null;
        return (
          <div className="surface p-6 sm:p-8 mt-4">
            <p className="eyebrow mb-1">Recommended · where to buy</p>
            <p className="text-sm text-arc-muted mb-4">
              Two exchanges Swami uses to add to his stack
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {exchanges.map((l) => (
                <AffiliateCard key={l.id} link={l} medium="investments-tab" />
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default InvestmentsTab;